import { withListOverflow } from "../../v2/components/ListOverflow.jsx";
export const IngestionHistoryPanel = withListOverflow(IngestionHistoryListOwner);
// src/features/data-health/IngestionHistoryPanel.jsx
//
// INGEST-HISTORY-SIMPLIFY-S1 — simplified ingestion history screen.
//
// Fetches from GET /api/ingestion-events. Renders:
//   one-line summary ("N imports · N staged · N need review")
//   a single Filter control (type + status + time, collapsed into one popover)
//   a search box (source or dataset)
//   a consolidated table: Source · Dataset · Result · Status · When · (actions)
//   per-row Resume/Discard for staged ("Pending review") sessions (admin-only)
//   bulk "Clear staged · N" button (admin-only)
//
// INGESTION-HISTORY-REVERT — admin-only one-click revert for Applied imports.
//   Revert button appears on Applied rows for admins.
//   Click → preview endpoint → ConfirmDialog → POST revert.
//   409 (superseded) surfaces inline next to the button, not in a dialog.
//
// Client-side filtering is applied after the initial fetch; the server
// supports the same params if load ever warrants server-side filtering.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { matchesFoldedQuery } from "../../utils/textSearch.js";
import { useNavigate } from "react-router-dom";
import { T } from "../../app/tokens.js";
import {
  listIngestionEvents,
  clearDiscardedIngestionEvents,
  clearIngestionEvent,
  getIngestionEventRevertPreview,
  revertIngestionEvent,
} from "../../data/ingestionEventsClient.js";
import {
  discardImportSession,
  discardAllPendingSessions,
  resumeImportSession,
} from "../../data/importSessionsClient.js";
import { IngestionTypeTag } from "../../components/IngestionTypeTag.jsx";
import { ErrorBanner } from "../../components/ErrorBanner.jsx";
import { ToastBanner } from "../../components/ToastBanner.jsx";
import { DataTabHeading } from "../../components/DataTabHeading.jsx";
import { SearchInput } from "../../v2/components/SearchInput.jsx";
import { ConsolidatedFilter } from "../../v2/components/ConsolidatedFilter.jsx";
import { log } from "../../utils/logger.js";

// Standard admin toolbar button — matches DataSection.jsx's BTN_BASE
// convention (Clear demo data / Hard-delete) so bulk-clear actions here
// read as ordinary buttons, not filter-style pills.
const BTN_BASE = {
  padding:      "6px 13px",
  borderRadius: 6,
  fontSize:     T.fsSm5,
  fontWeight:   T.fwSemibold,
  fontFamily:   "inherit",
  cursor:       "pointer",
  display:      "inline-flex",
  alignItems:   "center",
  gap:          5,
  border:       `1.5px solid ${T.border}`,
  background:   T.card,
  color:        T.dark,
};

// ─── Provenance badge (16 px circle, §11 pattern) ────────────────────────────

// Colours match docs/style-guide-components.html §11 .prov palette.
const PROV_COLOR = {
  S: T.blue,
  M: T.provenanceBadgeManual,
  P: T.purpleDeep,
  C: T.teal,
};

function ProvenanceBadge({ code }) {
  if (!code) return null;
  const bg = PROV_COLOR[code] || T.mid;
  return (
    <span
      data-testid={`prov-badge-${code}`}
      style={{
        display:         "inline-flex",
        alignItems:      "center",
        justifyContent:  "center",
        width:           16,
        height:          16,
        borderRadius:    "50%",
        background:      bg,
        color:           T.white,
        fontSize:        T.fsXxs,
        fontWeight:      T.fwBold,
        flexShrink:      0,
        verticalAlign:   "middle",
      }}
    >
      {code}
    </span>
  );
}

// ─── Status pill (§1b RAG tints) ──────────────────────────────────────────────

const STATUS_PILL = {
  Applied:          { bg: T.ragGreenBg,  color: T.green  },
  Healthy:          { bg: T.ragGreenBg,  color: T.green  },
  "Pending review": { bg: T.ragAmberBg,  color: T.ragAmber },
  Staged:           { bg: T.ragAmberBg,  color: T.ragAmber },
  Partial:          { bg: T.ragAmberBg,  color: T.ragAmber },
  Stale:            { bg: T.ragAmberBg,  color: T.ragAmber },
  Failed:           { bg: T.ragRedBg,    color: T.ragRed  },
  // Reverted: neutral purple — terminal but undone; distinct from Applied
  // green and Discarded default-grey.
  Reverted:         { bg: T.purpleBg,    color: T.purpleDeep },
};

// A row counts as "staged" whether it carries the current "Pending review"
// status or the legacy "Staged" status — both represent an import awaiting
// review/apply and both must be reflected in the staged count and cleared
// by "Clear staged".
function isStagedStatus(status) {
  return status === "Pending review" || status === "Staged";
}

function StatusPill({ status }) {
  const s = STATUS_PILL[status] || { bg: T.lightGray, color: T.mid };
  return (
    <span
      data-testid={`status-pill-${status}`}
      style={{
        display:      "inline-flex",
        alignItems:   "center",
        fontSize:     T.fsXs,
        fontWeight:   T.fwSemibold,
        padding:      "2px 10px",
        borderRadius: 6,
        background:   s.bg,
        color:        s.color,
        whiteSpace:   "nowrap",
      }}
    >
      {status}
    </span>
  );
}

// ─── Filter dimensions (ConsolidatedFilter config) ───────────────────────────

const FILTER_DIMENSIONS = [
  {
    id: "type",
    label: "Type",
    options: [
      { value: "csv",         label: "CSV" },
      { value: "file",        label: "FILE" },
      { value: "integration", label: "INTEGRATION" },
    ],
  },
  {
    id: "status",
    label: "Status",
    options: [
      { value: "Applied",         label: "Applied" },
      { value: "Reverted",        label: "Reverted" },
      { value: "Healthy",         label: "Healthy" },
      { value: "Pending review",  label: "Pending review" },
      { value: "Staged",          label: "Staged" },
      { value: "Partial",         label: "Partial" },
      { value: "Stale",           label: "Stale" },
      { value: "Failed",          label: "Failed" },
    ],
  },
  {
    id: "time",
    label: "Time",
    options: [
      { value: "24h", label: "Last 24h" },
      { value: "7d",  label: "Last 7d" },
      { value: "30d", label: "Last 30d" },
    ],
  },
];

const TIME_RANGE_DAYS = { "24h": 1, "7d": 7, "30d": 30 };

// The "time" dimension is logically single-select (a lower bound on when the
// event occurred) even though ConsolidatedFilter's checkboxes allow multiple
// selections; when a second time option is checked, keep only the newest one.
function normalizeFilterSelections(prev, next) {
  const prevTime = prev.time || [];
  const nextTime = next.time || [];
  if (nextTime.length <= 1) return next;
  const added = nextTime.find((v) => !prevTime.includes(v));
  return { ...next, time: [added ?? nextTime[nextTime.length - 1]] };
}

// ─── Formatters ───────────────────────────────────────────────────────────────

function fmtWhen(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
    + ", "
    + d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
}

function diffCountsLabel(applied, rejected, held) {
  if (applied == null && rejected == null && held == null) return null;
  const parts = [];
  if (applied  > 0) parts.push(`${applied} applied`);
  if (rejected > 0) parts.push(`${rejected} rejected`);
  if (held     > 0) parts.push(`${held} held`);
  return parts.length ? parts.join(" · ") : "0 applied";
}

// Consolidated "Result" column — plain-English phrasing, never raw
// "+N / ~N / skip N" symbols:
//   - post-review diff outcome available  → "335 applied · 5 rejected"
//   - still staged (no diff outcome yet)  → "177 rows staged"
//   - otherwise (direct sync/apply, no diff workflow) → "N applied"
// Statuses that legitimately mean "N applied" — the record counts reflect
// data actually written. Every other status (Discarded/Superseded/Failed/
// Stale/etc.) still carries the counts recorded at *staging* time (they are
// never cleared), so falling through to "N applied" for those statuses
// falsely implies the import succeeded. See INGESTION-HISTORY-STATUS-ACCURACY.
const APPLIED_RESULT_STATUSES = new Set(["Applied", "Healthy", "Partial"]);

function resultLabel(ev) {
  const diffOutcome = diffCountsLabel(ev.appliedCount, ev.rejectedCount, ev.heldCount);
  if (diffOutcome) return diffOutcome;

  const total = (ev.recordsAdded || 0) + (ev.recordsUpdated || 0) + (ev.recordsSkipped || 0);
  if (total === 0) return "—";
  if (isStagedStatus(ev.status)) {
    return `${total} row${total === 1 ? "" : "s"} staged`;
  }
  if (!APPLIED_RESULT_STATUSES.has(ev.status)) {
    return `${total} row${total === 1 ? "" : "s"} staged (${ev.status.toLowerCase()})`;
  }
  return `${total} applied`;
}

// Actor label must never surface a raw user UUID — a defensive
// client-side guard in case an older ingestion_events row was written
// before the server started resolving a display name.
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
function actorLabel(actor) {
  if (!actor || UUID_RE.test(actor)) return "system";
  return actor;
}

// Extract the numeric import session id from a "/imports/{id}" detailRef.
function sessionIdFromDetailRef(detailRef) {
  const m = /^\/imports\/(\d+)$/.exec(detailRef || "");
  return m ? parseInt(m[1], 10) : null;
}

// ─── Confirm dialog (reuses PendingImportsQueue's ConfirmDialog pattern) ─────

function ConfirmDialog({ testId, message, confirmLabel, busyLabel, onConfirm, onCancel, busy }) {
  return (
    <div
      data-testid={testId}
      style={{
        position: "fixed", inset: 0, zIndex: 1000,
        display: "flex", alignItems: "center", justifyContent: "center",
        background: "rgba(0,0,0,0.35)",
      }}
    >
      <div
        style={{
          background: T.white, borderRadius: 10, padding: "28px 32px",
          maxWidth: 420, width: "90%", boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
        }}
      >
        <p
          style={{ margin: "0 0 20px", fontSize: T.fsBase, color: T.dark, lineHeight: 1.5 }}
          data-testid={`${testId}-message`}
        >
          {message}
        </p>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button
            type="button"
            data-testid={`${testId}-cancel-btn`}
            disabled={busy}
            onClick={onCancel}
            style={{
              padding: "7px 16px", borderRadius: 6, fontSize: T.fsSm,
              fontWeight: T.fwSemibold, border: `1.5px solid ${T.border}`,
              background: T.bg, color: T.dark, cursor: "pointer",
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid={`${testId}-confirm-btn`}
            disabled={busy}
            onClick={onConfirm}
            style={{
              padding: "7px 16px", borderRadius: 6, fontSize: T.fsSm,
              fontWeight: T.fwSemibold, border: "none",
              background: T.red, color: T.white,
              cursor: busy ? "default" : "pointer",
              opacity: busy ? 0.6 : 1,
            }}
          >
            {busy ? busyLabel : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── IngestionHistoryPanel ────────────────────────────────────────────────────

function IngestionHistoryListOwner({ isAdmin = false, onResumeSession }) {
  const navigate = useNavigate();
  const [data,         setData]         = useState(null);  // { summary, events }
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState(null);
  const [filterSelections, setFilterSelections] = useState({}); // { type: [], status: [], time: [] }
  const [searchQ,      setSearchQ]      = useState("");
  const [clearingAll,  setClearingAll]  = useState(false);
  const [clearingId,   setClearingId]   = useState(null); // id of the row currently being cleared
  const [clearingStaged, setClearingStaged] = useState(false);
  const [discardingId, setDiscardingId] = useState(null); // session id currently being discarded
  const [resumingId, setResumingId] = useState(null); // session id currently being resumed
  const [confirmAction, setConfirmAction] = useState(null); // "staged" | "discarded" | null

  // Revert flow state
  const [revertPreviewId, setRevertPreviewId] = useState(null); // event id being fetched for preview
  const [revertConfirm,   setRevertConfirm]   = useState(null); // { eventId, preview: {...} }
  const [revertingId,     setRevertingId]      = useState(null); // event id currently being reverted
  const [revertRowError,  setRevertRowError]   = useState(null); // { eventId, message }
  const [revertToast,     setRevertToast]      = useState(null); // success message string

  const load = useCallback(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    listIngestionEvents()
      .then((d) => {
        if (cancelled) return;
        setData(d);
        log.api("IngestionHistoryPanel loaded", { total: d?.events?.length });
      })
      .catch((err) => {
        if (cancelled) return;
        log.error("IngestionHistoryPanel load failed", { err: String(err) });
        setError(err);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => load(), [load]);

  const handleClearDiscarded = useCallback(() => {
    setClearingAll(true);
    setError(null);
    clearDiscardedIngestionEvents()
      .then(() => {
        setConfirmAction(null);
        return load();
      })
      .catch((err) => {
        log.error("IngestionHistoryPanel clear discarded failed", { err: String(err) });
        setError(err);
      })
      .finally(() => setClearingAll(false));
  }, [load]);

  const handleClearOne = useCallback((id) => {
    setClearingId(id);
    setError(null);
    clearIngestionEvent(id)
      .then(() => load())
      .catch((err) => {
        log.error("IngestionHistoryPanel clear one failed", { id, err: String(err) });
        setError(err);
      })
      .finally(() => setClearingId(null));
  }, [load]);

  const handleClearStaged = useCallback(() => {
    setClearingStaged(true);
    setError(null);
    discardAllPendingSessions()
      .then(() => {
        setConfirmAction(null);
        return load();
      })
      .catch((err) => {
        log.error("IngestionHistoryPanel clear staged failed", { err: String(err) });
        setError(err);
      })
      .finally(() => setClearingStaged(false));
  }, [load]);

  const handleDiscardOne = useCallback((sessionId) => {
    setDiscardingId(sessionId);
    setError(null);
    discardImportSession(sessionId)
      .then(() => load())
      .catch((err) => {
        log.error("IngestionHistoryPanel discard session failed", { sessionId, err: String(err) });
        setError(err);
      })
      .finally(() => setDiscardingId(null));
  }, [load]);

  // Resume is audited server-side (admin-only) before the wizard navigates
  // into the session; only invoke onResumeSession once the audit succeeds.
  const handleResumeOne = useCallback((sessionId) => {
    setResumingId(sessionId);
    setError(null);
    resumeImportSession(sessionId)
      .then(() => onResumeSession?.(sessionId))
      .catch((err) => {
        log.error("IngestionHistoryPanel resume session failed", { sessionId, err: String(err) });
        setError(err);
      })
      .finally(() => setResumingId(null));
  }, [onResumeSession]);

  // ── Revert handlers ────────────────────────────────────────────────────────

  // Step 1: Fetch the preview and either show the confirm dialog or surface
  // the superseded error inline next to the row's Revert button.
  const handleRevertClick = useCallback((ev) => {
    const eventId = ev.id;
    setRevertPreviewId(eventId);
    setRevertRowError(null);

    getIngestionEventRevertPreview(eventId)
      .then((preview) => {
        if (preview.superseded) {
          // Surface superseded message inline — no confirm dialog.
          setRevertRowError({ eventId, message: preview.supersededMessage });
        } else {
          setRevertConfirm({ eventId, preview });
        }
      })
      .catch((err) => {
        // Server-side 409 also lands here (ConflictError).
        const msg = err?.message || "Could not fetch revert preview. Please try again.";
        setRevertRowError({ eventId, message: msg });
        log.error("IngestionHistoryPanel revert preview failed", { eventId, err: String(err) });
      })
      .finally(() => setRevertPreviewId(null));
  }, []);

  // Step 2: Execute the confirmed revert.
  const handleRevertConfirm = useCallback(() => {
    if (!revertConfirm) return;
    const { eventId } = revertConfirm;
    setRevertingId(eventId);

    revertIngestionEvent(eventId)
      .then((result) => {
        setRevertConfirm(null);
        setRevertToast(
          `Import reverted. ${result.rowsRemoved} row${result.rowsRemoved === 1 ? "" : "s"} removed.`
        );
        return load();
      })
      .catch((err) => {
        const msg = err?.message || "Revert failed. Please try again.";
        setRevertRowError({ eventId, message: msg });
        setRevertConfirm(null);
        log.error("IngestionHistoryPanel revert failed", { eventId, err: String(err) });
      })
      .finally(() => setRevertingId(null));
  }, [revertConfirm, load]);

  // Client-side filter — cheap for expected volume.
  const filtered = useMemo(() => {
    if (!data?.events) return [];
    const typeSel   = filterSelections.type   || [];
    const statusSel = filterSelections.status || [];
    const timeSel   = filterSelections.time   || [];
    let cutoff = null;
    if (timeSel.length === 1 && TIME_RANGE_DAYS[timeSel[0]]) {
      cutoff = Date.now() - TIME_RANGE_DAYS[timeSel[0]] * 24 * 60 * 60 * 1000;
    }
    return data.events.filter((ev) => {
      if (typeSel.length   && !typeSel.includes(ev.type))     return false;
      if (statusSel.length && !statusSel.includes(ev.status)) return false;
      if (searchQ) {
        const q = searchQ;
        const matchesSource  = matchesFoldedQuery(q, ev.sourceName);
        const matchesDataset = matchesFoldedQuery(q, ev.targetDataset);
        if (!matchesSource && !matchesDataset) return false;
      }
      if (cutoff != null && ev.occurredAt && new Date(ev.occurredAt).getTime() < cutoff) return false;
      return true;
    });
  }, [data, filterSelections, searchQ]);

  const summary = data?.summary || { total30d: 0, byType: {}, needsReview: 0 };
  const stagedCount = useMemo(
    () => (data?.events || []).filter((ev) => isStagedStatus(ev.status)).length,
    [data]
  );
  const discardedCount = useMemo(
    () => (data?.events || []).filter((ev) => ev.status === "Discarded").length,
    [data]
  );

  return (
    <div data-testid="ingestion-history-panel" style={{ display: "flex", flexDirection: "column", gap: 18 }}>

      <DataTabHeading
        description="Unified log of every CSV upload, PDF abstraction, and connector run."
      >
        Data ingestion history
      </DataTabHeading>

      <ErrorBanner error={error} onDismiss={() => setError(null)} />

      {loading && (
        <div
          data-testid="ingestion-history-loading"
          style={{ padding: 32, textAlign: "center", color: T.mid, fontSize: T.fsBase }}
        >
          Loading ingestion history…
        </div>
      )}

      {!loading && !error && (
        <>
          {/* One-line summary + headcount trend deep-link */}
          <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
            <div
              data-testid="ingestion-summary-line"
              style={{ fontSize: T.fsBase, color: T.dark, fontWeight: T.fwSemibold }}
            >
              {summary.total30d} import{summary.total30d === 1 ? "" : "s"}
              {" · "}
              {stagedCount} staged
              {" · "}
              {summary.needsReview} need review
            </div>
            <button
              type="button"
              data-testid="view-headcount-trend-link"
              onClick={() => {
                navigate("/reports/headcount-trend");
              }}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: 0,
                fontSize: T.fsSm5,
                color: T.blue,
                fontWeight: T.fwSemibold,
                marginLeft: "auto",
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
              }}
            >
              View headcount trend →
            </button>
          </div>

          {/* Controls row — standard SearchInput + ConsolidatedFilter pattern, Search then Filter */}
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <SearchInput
              value={searchQ}
              onChange={setSearchQ}
              placeholder="Search source or dataset…"
              testId="ingestion-search-input"
              width="auto"
              style={{ minWidth: 200 }}
            />

            <ConsolidatedFilter
              testId="ingestion-filter-btn"
              dimensions={FILTER_DIMENSIONS}
              selections={filterSelections}
              onChange={(next) => setFilterSelections((prev) => normalizeFilterSelections(prev, next))}
            />

            <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
              {isAdmin && (
                <button
                  type="button"
                  data-testid="clear-staged-btn"
                  onClick={() => setConfirmAction("staged")}
                  disabled={clearingStaged || stagedCount === 0}
                  style={{
                    ...BTN_BASE,
                    cursor:  (clearingStaged || stagedCount === 0) ? "default" : "pointer",
                    opacity: (clearingStaged || stagedCount === 0) ? 0.5 : 1,
                  }}
                >
                  {clearingStaged ? "Clearing…" : `Clear staged · ${stagedCount}`}
                </button>
              )}

              {isAdmin && (
                <button
                  type="button"
                  data-testid="clear-discarded-btn"
                  onClick={() => setConfirmAction("discarded")}
                  disabled={clearingAll || discardedCount === 0}
                  style={{
                    ...BTN_BASE,
                    cursor:  (clearingAll || discardedCount === 0) ? "default" : "pointer",
                    opacity: (clearingAll || discardedCount === 0) ? 0.5 : 1,
                  }}
                >
                  {clearingAll ? "Clearing…" : "Clear discarded"}
                </button>
              )}
            </div>
          </div>

          {/* Table */}
          <div
            data-testid="ingestion-history-table-wrapper"
            style={{
              background:   T.card,
              border:       `1px solid ${T.border}`,
              borderRadius: 10,
              overflow:     "hidden",
            }}
          >
            {filtered.length === 0 ? (
              <div
                data-testid="ingestion-history-empty"
                style={{ padding: 32, textAlign: "center", color: T.mid, fontSize: T.fsBase }}
              >
                {data.events.length === 0
                  ? "No ingestion events recorded yet."
                  : "No events match the current filters."}
              </div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table
                  data-testid="ingestion-history-table"
                  style={{ width: "100%", borderCollapse: "collapse", fontSize: T.fsSm }}
                >
                  <thead>
                    <tr style={{ background: T.hoverBg, borderBottom: `2px solid ${T.border}` }}>
                      {["Source", "Dataset", "Result", "Status", "When", ""].map((h) => (
                        <th
                          key={h}
                          style={{
                            padding:       "8px 12px",
                            textAlign:     "left",
                            fontSize:      T.fsXs,
                            fontWeight:    T.fwBold,
                            textTransform: "uppercase",
                            letterSpacing: 0.5,
                            color:         T.mid,
                            whiteSpace:    "nowrap",
                          }}
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((ev, i) => {
                      const sessionId = sessionIdFromDetailRef(ev.detailRef);
                      const isStaged  = isStagedStatus(ev.status) && sessionId != null;
                      const isApplied = ev.status === "Applied" && sessionId != null;
                      const rowRevertError =
                        revertRowError?.eventId === ev.id ? revertRowError.message : null;
                      return (
                      <tr
                        key={ev.id}
                        data-testid={`ingestion-row-${ev.id}`}
                        style={{
                          borderBottom: i < filtered.length - 1
                            ? `1px solid ${T.border}`
                            : "none",
                        }}
                      >
                        {/* Source: type tag + name (linked if detailRef) */}
                        <td style={{ padding: "10px 12px", maxWidth: 320, overflow: "hidden" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 7, overflow: "hidden", minWidth: 0 }}>
                            <IngestionTypeTag type={ev.type} />
                            {ev.detailRef ? (
                              <a
                                href={ev.detailRef}
                                target="_blank"
                                rel="noopener noreferrer"
                                title={ev.sourceName}
                                style={{ fontWeight: T.fwSemibold, color: T.dark, textDecoration: "underline", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                              >
                                {ev.sourceName}
                              </a>
                            ) : (
                              <span title={ev.sourceName} style={{ fontWeight: T.fwSemibold, color: T.dark, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                {ev.sourceName}
                              </span>
                            )}
                          </div>
                        </td>

                        {/* Dataset + provenance badge */}
                        <td style={{ padding: "10px 12px", whiteSpace: "nowrap" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            {ev.provenance && <ProvenanceBadge code={ev.provenance} />}
                            <span style={{ color: T.mid }}>{ev.targetDataset}</span>
                          </div>
                        </td>

                        {/* Result — merged records + diff-outcome counts */}
                        <td
                          data-testid={`result-${ev.id}`}
                          style={{ padding: "10px 12px", color: T.mid, whiteSpace: "nowrap", fontSize: T.fsXs }}
                        >
                          {resultLabel(ev)}
                        </td>

                        {/* Status pill */}
                        <td style={{ padding: "10px 12px", whiteSpace: "nowrap" }}>
                          <StatusPill status={ev.status} />
                        </td>

                        {/* When — actor folded in as muted subtext, never a raw UUID */}
                        <td style={{ padding: "10px 12px", color: T.mid, whiteSpace: "nowrap" }}>
                          <div>{fmtWhen(ev.occurredAt)}</div>
                          <div
                            data-testid={`actor-${ev.id}`}
                            style={{ fontSize: T.fsXxs, color: T.mid }}
                          >
                            by {actorLabel(ev.actor)}
                          </div>
                        </td>

                        {/* Actions: Resume/Discard for staged rows, Revert for applied, clear for Discarded */}
                        <td style={{ padding: "10px 12px", whiteSpace: "nowrap" }}>
                          {isAdmin && isStaged && (
                            <div style={{ display: "flex", gap: 6 }}>
                              <button
                                type="button"
                                data-testid={`resume-row-btn-${sessionId}`}
                                onClick={() => handleResumeOne(sessionId)}
                                disabled={resumingId === sessionId}
                                title="Resume this import in the wizard"
                                style={{
                                  border:       `1px solid ${T.blue}`,
                                  background:   T.card,
                                  color:        T.blue,
                                  cursor:       resumingId === sessionId ? "default" : "pointer",
                                  opacity:      resumingId === sessionId ? 0.5 : 1,
                                  fontSize:     T.fsXxs,
                                  fontWeight:   T.fwBold,
                                  padding:      "3px 9px",
                                  borderRadius: 14,
                                }}
                              >
                                {resumingId === sessionId ? "…" : "Resume"}
                              </button>
                              <button
                                type="button"
                                data-testid={`discard-row-btn-${sessionId}`}
                                onClick={() => handleDiscardOne(sessionId)}
                                disabled={discardingId === sessionId}
                                title="Discard this staged session"
                                style={{
                                  border:       `1px solid ${T.ragRed}`,
                                  background:   T.card,
                                  color:        T.ragRed,
                                  cursor:       discardingId === sessionId ? "default" : "pointer",
                                  opacity:      discardingId === sessionId ? 0.5 : 1,
                                  fontSize:     T.fsXxs,
                                  fontWeight:   T.fwBold,
                                  padding:      "3px 9px",
                                  borderRadius: 14,
                                }}
                              >
                                {discardingId === sessionId ? "…" : "Discard"}
                              </button>
                            </div>
                          )}

                          {isAdmin && isApplied && (
                            <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-start" }}>
                              <button
                                type="button"
                                data-testid={`revert-row-btn-${ev.id}`}
                                onClick={() => handleRevertClick(ev)}
                                disabled={revertPreviewId === ev.id || revertingId === ev.id}
                                title="Undo this applied import"
                                style={{
                                  border:       `1px solid ${T.purpleDeep}`,
                                  background:   T.card,
                                  color:        T.purpleDeep,
                                  cursor:
                                    revertPreviewId === ev.id || revertingId === ev.id
                                      ? "default"
                                      : "pointer",
                                  opacity:
                                    revertPreviewId === ev.id || revertingId === ev.id
                                      ? 0.5
                                      : 1,
                                  fontSize:     T.fsXxs,
                                  fontWeight:   T.fwBold,
                                  padding:      "3px 9px",
                                  borderRadius: 14,
                                }}
                              >
                                {revertPreviewId === ev.id ? "…" : "Revert"}
                              </button>

                              {/* Inline superseded / revert error for this row */}
                              {rowRevertError && (
                                <div
                                  data-testid={`revert-row-error-${ev.id}`}
                                  style={{
                                    fontSize:   T.fsXxs,
                                    color:      T.ragRed,
                                    maxWidth:   220,
                                    whiteSpace: "normal",
                                    lineHeight: 1.4,
                                  }}
                                >
                                  {rowRevertError}
                                </div>
                              )}
                            </div>
                          )}

                          {isAdmin && ev.status === "Discarded" && (
                            <button
                              type="button"
                              data-testid={`clear-row-btn-${ev.id}`}
                              onClick={() => handleClearOne(ev.id)}
                              disabled={clearingId === ev.id}
                              title="Clear this discarded session"
                              aria-label={`Clear discarded session ${ev.sourceName}`}
                              style={{
                                border:     "none",
                                background: "transparent",
                                color:      T.ragRed,
                                cursor:     clearingId === ev.id ? "default" : "pointer",
                                fontSize:   T.fsSm,
                                fontWeight: T.fwBold,
                                opacity:    clearingId === ev.id ? 0.5 : 1,
                                padding:    "2px 6px",
                              }}
                            >
                              {clearingId === ev.id ? "…" : "×"}
                            </button>
                          )}
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {/* ── Confirm dialogs ─────────────────────────────────────────────────── */}

      {confirmAction === "staged" && (
        <ConfirmDialog
          testId="clear-staged-confirm-dialog"
          message={`Clear ${stagedCount} staged import${stagedCount === 1 ? "" : "s"}? This discards every pending review session and cannot be undone.`}
          confirmLabel="Clear staged"
          busyLabel="Clearing…"
          busy={clearingStaged}
          onCancel={() => { if (!clearingStaged) setConfirmAction(null); }}
          onConfirm={handleClearStaged}
        />
      )}

      {confirmAction === "discarded" && (
        <ConfirmDialog
          testId="clear-discarded-confirm-dialog"
          message={`Clear ${discardedCount} discarded event${discardedCount === 1 ? "" : "s"}? This permanently removes them from the log and cannot be undone.`}
          confirmLabel="Clear discarded"
          busyLabel="Clearing…"
          busy={clearingAll}
          onCancel={() => { if (!clearingAll) setConfirmAction(null); }}
          onConfirm={handleClearDiscarded}
        />
      )}

      {/* Revert confirmation dialog — shown after a successful preview fetch */}
      {revertConfirm && (
        <ConfirmDialog
          testId="revert-confirm-dialog"
          message={
            `This will remove ${revertConfirm.preview.rowCount} row${
              revertConfirm.preview.rowCount === 1 ? "" : "s"
            } applied on ${fmtWhen(revertConfirm.preview.occurredAt)} from ` +
            `${revertConfirm.preview.dataset}. Undo?`
          }
          confirmLabel="Revert import"
          busyLabel="Reverting…"
          busy={revertingId === revertConfirm.eventId}
          onCancel={() => {
            if (revertingId !== revertConfirm.eventId) setRevertConfirm(null);
          }}
          onConfirm={handleRevertConfirm}
        />
      )}

      {/* Fixed-position success toast */}
      {revertToast && (
        <ToastBanner
          testId="revert-success-toast"
          message={revertToast}
          type="success"
          onDismiss={() => setRevertToast(null)}
        />
      )}
    </div>
  );
}
